// WS5 PR3.3b — First-run coach overlay (C1).
//
// Overlay non-modal top-center mostrato al primo accesso a una scena con
// `viewboxes >= 1` su desktop. Insegna in 3-5 secondi:
//   - che siamo in modalita' FP (prima persona)
//   - come entrare in walk (CTA "Entra nella stanza")
//   - come muoversi (WASD)
//
// Scelte chiave (cfr. PLAN_WS5 §8.6 + decisione utente C1):
//   - Desktop only in PR3.3b. Mobile = PR4 (richiede touch FP wiring).
//   - CTA "Entra" chiama `onAcquirePointerLock` (orchestrator.toggleWalk)
//     nella STESSA gesture, per evitare che il browser neghi pointer-lock
//     (policy: user activation richiesta).
//   - NON focus-trap (Codex iter3 W3): coach overlay deve permettere altri
//     interaction (es. apertura toolbar). Solo CTA e Close sono pointer-events.
//   - Auto-dismiss dopo 8 secondi (non bloccare l'utente, se vuole solo
//     guardare).
//   - ESC dismiss.
//   - Dismiss persistente via localStorage flag (caller deride visible).
//
// a11y:
//   - role="region" (non dialog → non focus trap).
//   - aria-label localizzato.
//   - CTA primary button con focus visible.
//   - Close button con aria-label.

import { t } from '../../i18n/init.js';
import type { ComponentInstance } from '../component.js';

export interface FirstRunCoachProps {
	visible: boolean;
	embed: boolean;
	onAcquirePointerLock: () => void;
	onDismiss: () => void;
}

const PRIMARY_BG = '#60a5fa';
const PRIMARY_BG_HOVER = '#3b82f6';

export function mountFirstRunCoach(
	root: HTMLElement,
	initial: FirstRunCoachProps,
): ComponentInstance<FirstRunCoachProps> {
	const wrap = document.createElement('section');
	wrap.classList.add('viewer-ui-interactive');
	wrap.setAttribute('role', 'region');
	wrap.setAttribute('aria-label', t('viewer.coach.aria_label'));
	wrap.style.cssText = initial.embed
		? 'position:fixed;top:1rem;left:50%;transform:translateX(-50%);max-width:24rem;background:rgba(15,15,17,0.92);backdrop-filter:blur(10px);border:1px solid rgba(96,165,250,0.35);border-radius:0.75rem;padding:1rem 1.25rem;z-index:12;font-family:system-ui,sans-serif;color:#f4f4f5;pointer-events:none'
		: 'position:fixed;top:4rem;left:50%;transform:translateX(-50%);max-width:28rem;background:rgba(15,15,17,0.92);backdrop-filter:blur(10px);border:1px solid rgba(96,165,250,0.35);border-radius:1rem;padding:1.5rem 1.75rem;z-index:12;font-family:system-ui,sans-serif;color:#f4f4f5;pointer-events:none';
	wrap.style.display = 'none';

	// Close button (top-right) — pointer-events:auto (override wrap).
	const closeBtn = document.createElement('button');
	closeBtn.type = 'button';
	closeBtn.setAttribute('aria-label', t('viewer.coach.dismiss_label'));
	closeBtn.style.cssText =
		'position:absolute;top:0.5rem;right:0.625rem;width:1.75rem;height:1.75rem;border:none;background:transparent;color:#a1a1aa;cursor:pointer;font-size:1.25rem;line-height:1;border-radius:9999px;pointer-events:auto;transition:color 120ms ease,background 120ms ease';
	closeBtn.textContent = '×';
	closeBtn.addEventListener('mouseenter', () => {
		closeBtn.style.color = '#f4f4f5';
		closeBtn.style.background = 'rgba(255,255,255,0.08)';
	});
	closeBtn.addEventListener('mouseleave', () => {
		closeBtn.style.color = '#a1a1aa';
		closeBtn.style.background = 'transparent';
	});
	wrap.appendChild(closeBtn);

	// Title.
	const title = document.createElement('h2');
	title.style.cssText =
		'margin:0 0 0.5rem 0;font-size:1.125rem;font-weight:600;color:#bfdbfe;padding-right:2rem';
	title.textContent = t('viewer.coach.title');
	wrap.appendChild(title);

	// Body copy.
	const body = document.createElement('p');
	body.style.cssText =
		'margin:0 0 1rem 0;font-size:0.875rem;line-height:1.5;color:#d4d4d8';
	body.textContent = t('viewer.coach.body_desktop');
	wrap.appendChild(body);

	// Primary CTA.
	const cta = document.createElement('button');
	cta.type = 'button';
	cta.style.cssText = `display:block;width:100%;padding:0.625rem 1rem;border:none;border-radius:0.5rem;background:${PRIMARY_BG};color:#0f0f11;font-weight:600;font-size:0.875rem;cursor:pointer;pointer-events:auto;transition:background 120ms ease`;
	cta.textContent = t('viewer.coach.cta');
	cta.addEventListener('mouseenter', () => {
		cta.style.background = PRIMARY_BG_HOVER;
	});
	cta.addEventListener('mouseleave', () => {
		cta.style.background = PRIMARY_BG;
	});
	wrap.appendChild(cta);

	root.appendChild(wrap);

	let autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
	let currentProps: FirstRunCoachProps = initial;

	const scheduleAutoDismiss = (): void => {
		if (autoDismissTimer !== null) clearTimeout(autoDismissTimer);
		// 8 secondi: lungo abbastanza per leggere, breve abbastanza per non
		// essere fastidioso a chi vuole solo guardare la scena.
		autoDismissTimer = setTimeout(() => {
			currentProps.onDismiss();
		}, 8000);
	};

	const clearAutoDismiss = (): void => {
		if (autoDismissTimer !== null) {
			clearTimeout(autoDismissTimer);
			autoDismissTimer = null;
		}
	};

	closeBtn.addEventListener('click', () => {
		clearAutoDismiss();
		currentProps.onDismiss();
	});

	cta.addEventListener('click', () => {
		// CRITICAL (PLAN §8.6 + Codex PR_WS5 iter3 C2): pointer-lock va
		// richiesto NELLA STESSA gesture click. Per garantirlo, chiamiamo
		// onAcquirePointerLock() PRIMA del dismiss (che invece scrive
		// localStorage e nasconde l'overlay).
		clearAutoDismiss();
		currentProps.onAcquirePointerLock();
		currentProps.onDismiss();
	});

	const onKey = (ev: KeyboardEvent): void => {
		if (wrap.style.display === 'none') return;
		if (ev.key === 'Escape') {
			// Codex PR3.3b W1: stop propagation per evitare doppia azione con
			// shortcut ESC globali (es. exitFullscreen in orchestrator). Quando
			// il coach e' visibile, ESC = dismiss coach (e basta).
			ev.preventDefault();
			ev.stopPropagation();
			clearAutoDismiss();
			currentProps.onDismiss();
		}
	};
	// capture=true: intercetta ESC PRIMA degli handler globali su window.
	document.addEventListener('keydown', onKey, { capture: true });

	const apply = (props: FirstRunCoachProps): void => {
		currentProps = props;
		const shouldShow = props.visible;
		const isShowing = wrap.style.display === 'block';
		if (shouldShow && !isShowing) {
			wrap.style.display = 'block';
			scheduleAutoDismiss();
		} else if (!shouldShow && isShowing) {
			wrap.style.display = 'none';
			clearAutoDismiss();
		}
	};

	apply(initial);

	return {
		update(props: FirstRunCoachProps) {
			apply(props);
		},
		cleanup() {
			clearAutoDismiss();
			document.removeEventListener('keydown', onKey, { capture: true });
			wrap.remove();
		},
	};
}
